import React, { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';

import { OPEN_COOKIE_PREFERENCES_EVENT } from '@/components/consent/openCookiePreferences';
import {
  consentCopy,
  getCookiesPolicyUrl,
  getPrivacyPolicyUrl,
  getStoredConsent,
  hasStoredConsent,
  initLogRocketIfConsented,
  marketingLinkProps,
  onConsentChanged,
  saveConsent
} from '@/lib/consent';

import './cookie-consent.css';

const CookieConsent: React.FC = () => {
  const { t } = useTranslation('common');
  const [bannerVisible, setBannerVisible] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  const syncFromStorage = useCallback(() => {
    const stored = getStoredConsent();
    setBannerVisible(!hasStoredConsent());
    setAnalyticsEnabled(stored?.analytics ?? false);
  }, []);

  useEffect(() => {
    syncFromStorage();
    return onConsentChanged(() => {
      syncFromStorage();
    });
  }, [syncFromStorage]);

  useEffect(() => {
    const openPreferences = () => {
      const stored = getStoredConsent();
      setAnalyticsEnabled(stored?.analytics ?? false);
      setPreferencesOpen(true);
      setBannerVisible(false);
    };

    window.addEventListener(OPEN_COOKIE_PREFERENCES_EVENT, openPreferences);
    return () => window.removeEventListener(OPEN_COOKIE_PREFERENCES_EVENT, openPreferences);
  }, []);

  const handleAcceptAll = () => {
    saveConsent('accept-all');
    initLogRocketIfConsented();
    setBannerVisible(false);
    setPreferencesOpen(false);
  };

  const handleRejectNonEssential = () => {
    saveConsent('reject-non-essential');
    setBannerVisible(false);
    setPreferencesOpen(false);
  };

  const handleSavePreferences = () => {
    saveConsent('custom', analyticsEnabled);
    initLogRocketIfConsented();
    setPreferencesOpen(false);
    setBannerVisible(false);
  };

  const privacyUrl = getPrivacyPolicyUrl();
  const cookiesPolicyUrl = getCookiesPolicyUrl();

  return (
    <>
      {bannerVisible && (
        <aside className="cookie-consent" role="dialog" aria-labelledby="cookie-consent-title" aria-live="polite">
          <div className="cookie-consent__inner">
            <div className="cookie-consent__content">
              <h2 id="cookie-consent-title" className="cookie-consent__title">
                {consentCopy.bannerTitle}
              </h2>
              <p className="cookie-consent__description">
                {consentCopy.bannerDescription} <a {...marketingLinkProps(privacyUrl)}>{consentCopy.privacyPolicy}</a>.
              </p>
            </div>
            <div className="cookie-consent__actions">
              <button
                type="button"
                className="btn btn--secondary cookie-consent__btn"
                onClick={handleRejectNonEssential}
              >
                {consentCopy.rejectNonEssential}
              </button>
              <button
                type="button"
                className="btn btn--ghost cookie-consent__btn"
                onClick={() => {
                  setAnalyticsEnabled(getStoredConsent()?.analytics ?? false);
                  setPreferencesOpen(true);
                }}
              >
                {consentCopy.managePreferences}
              </button>
              <button type="button" className="btn btn--primary cookie-consent__btn" onClick={handleAcceptAll}>
                {consentCopy.acceptAll}
              </button>
            </div>
          </div>
        </aside>
      )}

      <Dialog.Root open={preferencesOpen} onOpenChange={setPreferencesOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="cookie-consent-dialog__overlay" />
          <Dialog.Content className="cookie-consent-dialog" aria-describedby="cookie-consent-dialog-desc">
            <Dialog.Title className="cookie-consent-dialog__title">{consentCopy.preferencesTitle}</Dialog.Title>
            <Dialog.Description id="cookie-consent-dialog-desc" className="cookie-consent-dialog__description">
              {consentCopy.preferencesDescription}{' '}
              <a {...marketingLinkProps(cookiesPolicyUrl)}>{consentCopy.cookiePreferencesLink}</a>.
            </Dialog.Description>

            <ul className="cookie-consent-dialog__categories">
              <li className="cookie-consent-dialog__category">
                <div className="cookie-consent-dialog__category-header">
                  <h3>{consentCopy.necessaryTitle}</h3>
                  <span className="cookie-consent-dialog__always-on">Always on</span>
                </div>
                <p>{consentCopy.necessaryDescription}</p>
              </li>
              <li className="cookie-consent-dialog__category">
                <div className="cookie-consent-dialog__category-header">
                  <h3>{consentCopy.functionalTitle}</h3>
                  <span className="cookie-consent-dialog__always-on">Always on</span>
                </div>
                <p>{consentCopy.functionalDescription}</p>
              </li>
              <li className="cookie-consent-dialog__category">
                <div className="cookie-consent-dialog__category-header">
                  <h3>{consentCopy.analyticsTitle}</h3>
                  <label className="cookie-consent-dialog__toggle">
                    <input
                      type="checkbox"
                      checked={analyticsEnabled}
                      onChange={(event) => setAnalyticsEnabled(event.target.checked)}
                    />
                    <span>{analyticsEnabled ? 'On' : 'Off'}</span>
                  </label>
                </div>
                <p>{consentCopy.analyticsDescription}</p>
              </li>
            </ul>

            <div className="cookie-consent-dialog__actions">
              <button type="button" className="btn btn--secondary" onClick={handleRejectNonEssential}>
                {consentCopy.rejectNonEssential}
              </button>
              <button type="button" className="btn btn--primary" onClick={handleSavePreferences}>
                {consentCopy.savePreferences}
              </button>
            </div>

            <Dialog.Close className="cookie-consent-dialog__close" aria-label={t('close')}>
              ×
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
};

export default CookieConsent;
